// #2121-spec-kits
import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, Label, Form, Input, Button, Badge, UncontrolledTooltip, Spinner,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { translate } from 'react-translate';
import { actionGetPractices } from 'store/actions/practicesActions';
import { actionGetKitTypes, actionGetKitsCount } from 'store/actions/kitsActions';
import {
  actionSetEditOrder,
  actionOrderSubmitForm,
  actionGetReservedKits,
  actionSetReservedKits,
  actionAutoAssignKits,
  actionOrderSubmitEdition,
  actionGetOrder,
  actionGetOrderStateMachine,
} from 'store/actions/practiceOrdersActions';
import KitOverview from './KitOverview';
import PracticeOrdersDeliveryForm from './PracticeOrdersDeliveryForm';
import { toast } from 'react-toastify';
import moment from 'moment';

const STATUS_TONE = {
  o_st_pending:           'secondary',
  o_st_in_preparation:    'info',
  o_st_readytodeliver:    'primary',
  o_st_inpostmail:        'warning',
  o_st_receivedbypractice:'success',
  o_st_cancel:            'danger',
};

const PracticeOrdersForm = (props) => {
  const dispatch = useDispatch();
  const { t } = props;
  const history = useHistory();
  const { id } = useParams();

  const { orderEdit, reservedKits, kitDeleteLoading, stateMachine } = useSelector((s) => s.practiceOrdersReducer);
  const kitTypes  = useSelector((s) => s.kitsReducer.types);
  const kitsCount = useSelector((s) => s.kitsReducer.kitsCount);
  const { practices } = useSelector((s) => s.practicesReducer.practices);

  const [practicePick, setPracticePick] = useState('');
  const [edited, setEdited]             = useState(false);
  const [countryPick, setCountryPick]   = useState(null);

  const { register, errors, control, handleSubmit } = useForm({
    defaultValues: {
      practice: Object.keys(orderEdit).length ? orderEdit.ordered_by.practice_id : '',
    },
  });

  const onSubmit = (data) => {
    const formData = { ...data, country: countryPick };
    if (Object.keys(orderEdit).length) {
      dispatch(actionOrderSubmitEdition({ ...formData, _id: orderEdit._id }));
      setEdited(false);
    } else {
      dispatch(actionOrderSubmitForm(formData));
      setEdited(false);
    }
  };

  useEffect(() => {
    if (!Object.keys(orderEdit).length && id) dispatch(actionGetOrder(id));
    dispatch(actionGetPractices(0, 999));
    if (!Object.keys(kitTypes).length)     dispatch(actionGetKitTypes());
    if (!Object.keys(stateMachine).length) dispatch(actionGetOrderStateMachine());
    dispatch(actionGetKitsCount());
    if (Object.keys(orderEdit).length && !Object.keys(reservedKits).length) {
      dispatch(actionGetReservedKits(orderEdit._id));
    }
    return () => {
      dispatch(actionSetEditOrder({}));
      dispatch(actionSetReservedKits({}));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(orderEdit).length) {
      if (id) {
        if (practices && practices.length) {
          const practiceSelect = document.getElementById('practice');
          if (practiceSelect) {
            practiceSelect.value = orderEdit.ordered_by.practice_id;
            practiceSelect.dispatchEvent(new Event('change'));
          }
        }
        if (kitTypes && Object.keys(kitTypes).length) {
          Object.keys(kitTypes).forEach((k) => {
            const requested = orderEdit.requested_kits.find((r) => r.kit_type_id === kitTypes[k].value);
            const typeNumber = document.getElementById(k);
            if (typeNumber && requested) {
              typeNumber.value = requested.requested_quantity;
              typeNumber.dispatchEvent(new Event('change'));
            }
          });
        }
      }
      const statusSelect = document.getElementById('status');
      if (statusSelect) statusSelect.value = orderEdit.status.status_id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderEdit, kitTypes, kitsCount, practices]);

  const autoAssignKits = () => dispatch(actionAutoAssignKits(orderEdit._id));

  const copyId = (e, val) => {
    e.preventDefault();
    navigator.clipboard.writeText(val);
    toast.success(t('id-copied') || 'Copied');
  };

  const isEditing  = !!Object.keys(orderEdit).length;
  const tone       = isEditing ? (STATUS_TONE[orderEdit.status.status_id] || 'secondary') : 'secondary';
  const isCancelled = isEditing && orderEdit.status.status_id === 'o_st_cancel';

  return (
    <div className="content kit-form-page order-form-page mt-3">

      {/* Breadcrumb */}
      <div className="kit-form-breadcrumb">
        <button
          type="button"
          className="kit-detail-back"
          onClick={(e) => { e.preventDefault(); history.goBack(); }}
        >
          <i className="now-ui-icons arrows-1_minimal-left" />
          <span>{t('back-to-list') || 'Back to list'}</span>
        </button>
        <span>{t('title-header') || 'Practice Orders'}</span>
        <i className="now-ui-icons arrows-1_minimal-right" style={{ fontSize: '0.7rem' }} />
        <span style={{ color: '#2c2c2c', fontWeight: 600, letterSpacing: '0.04em' }}>
          {isEditing ? String(orderEdit._id).slice(-6).toUpperCase() : (t('new-order') || 'New order')}
        </span>
      </div>

      {/* Hero */}
      {isEditing && (
        <Card className="kit-form-hero">
          <CardBody>
            <div className="kit-form-hero-flex">
              <div className="kit-form-hero-icon">
                <i className="now-ui-icons shopping_delivery-fast" />
              </div>
              <div className="kit-form-hero-titles">
                <div className="kit-form-hero-friendly" style={{ fontSize: '1.4rem' }}>
                  {orderEdit.ordered_by && orderEdit.ordered_by.practice_name}
                </div>
                <div className="kit-form-hero-sub">
                  <div>
                    <span className="kit-form-hero-label">{t('order-id') || 'Order ID'}</span>
                    <code>{orderEdit._id}</code>
                    <button
                      type="button"
                      className="kit-detail-copy-btn"
                      onClick={(e) => copyId(e, orderEdit._id)}
                      id="copy-order-id"
                      aria-label="Copy"
                    >
                      <i className="now-ui-icons files_single-copy-04" />
                    </button>
                    <UncontrolledTooltip target="copy-order-id">{t('copy-btn') || 'Copy ID'}</UncontrolledTooltip>
                  </div>
                  {orderEdit.created_at && (
                    <div>
                      <i className="now-ui-icons ui-1_calendar-60" style={{ color: '#1c8ee0', marginRight: '0.3rem' }} />
                      <span>{moment(orderEdit.created_at).format('DD-MM-YYYY HH:mm:ss')}</span>
                    </div>
                  )}
                </div>
              </div>
              <Badge color={tone} pill style={{
                fontSize: '0.78rem',
                padding: '0.5rem 0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                {t(orderEdit.status.status_id)}
              </Badge>
            </div>
          </CardBody>
        </Card>
      )}

      <Form id="order-form" name="order-form" onSubmit={handleSubmit(onSubmit)}>

        {/* Order info */}
        <Card className="kit-form-card">
          <CardBody>
            <div className="kit-form-card-header">
              <i className="now-ui-icons ui-2_settings-90" />
              <span>{t('order-info') || 'Order Info'}</span>
            </div>
            <div className="kit-form-fields">
              <div>
                <Label for="practice">{t('practice')}</Label>
                <Input
                  type="select"
                  name="practice"
                  id="practice"
                  innerRef={register}
                  disabled={isEditing}
                  onChange={(e) => { setEdited(true); setPracticePick(e.target.value); }}
                >
                  <option value="">-</option>
                  {practices && practices.length ? practices.map((p, i) => (
                    <option key={`p-option-${i}`} value={p._id}>{p.name}</option>
                  )) : null}
                </Input>

                <Label for="status">{t('status')}</Label>
                <Input
                  type="select"
                  name="status"
                  id="status"
                  innerRef={register}
                  onChange={() => setEdited(true)}
                  disabled={isCancelled}
                >
                  {Object.keys(stateMachine).length
                    ? stateMachine[isEditing ? orderEdit.status.status_id : 'o_st_in_preparation'].map((st, i) => (
                        <option
                          key={`st-opt-${i}`}
                          value={st}
                          selected={isEditing ? orderEdit.status.status_id === st : false}
                        >
                          {t(st)}
                        </option>
                      ))
                    : null}
                </Input>
              </div>

              <div>
                <Label for="notes">{t('notes')}</Label>
                <Input
                  type="textarea"
                  name="notes"
                  className="notes-field"
                  id="notes"
                  innerRef={register}
                  onChange={(e) => { setEdited(true) }}
                  defaultValue={orderEdit.notes}
                  disabled={isCancelled}
                >
                </Input>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Delivery info */}
        <Card className="kit-form-card">
          <CardBody>
            <div className="kit-form-card-header">
              <i className="now-ui-icons location_pin" />
              <span>{t('delivery-info')}</span>
            </div>
            <PracticeOrdersDeliveryForm
              practicePick={practicePick}
              orderEdit={orderEdit}
              setCountryPick={setCountryPick}
              setEdited={setEdited}
              register={register}
              errors={errors}
            />
          </CardBody>
        </Card>

        {/* Kits requested */}
        <Card className="kit-form-card">
          <CardBody>
            <div className="kit-form-card-header">
              <i className="now-ui-icons shopping_box" />
              <span>{t('available-kits')}</span>
            </div>

            <div className="order-kits-grid">
              {kitTypes && Object.keys(kitTypes).length ? Object.keys(kitTypes).map((k) => {
                const requested = isEditing
                  ? (orderEdit.requested_kits.find((r) => r.kit_type_id === kitTypes[k].value) || {}).requested_quantity || 0
                  : 0;
                const available = kitsCount[kitTypes[k].value] || 0;
                const noStock = available < 1;
                return (
                  <div className={`order-kit-row ${noStock ? 'no-stock' : ''}`} key={`row-${k}`}>
                    <div className="order-kit-name">{kitTypes[k].value}</div>
                    <Input
                      type="number"
                      name={k}
                      id={k}
                      innerRef={register}
                      defaultValue={requested}
                      min={0}
                      onChange={() => setEdited(true)}
                      disabled={isCancelled}
                      className="order-kit-input"
                    />
                    <div className="order-kit-availability">
                      {t('available')}: <span className={`order-kit-count ${noStock ? 'no-stock' : ''}`}>{available}</span>
                    </div>
                  </div>
                );
              }) : null}
            </div>
          </CardBody>
        </Card>

        {/* Reserved kits (solo en edit con datos) */}
        {reservedKits && Object.keys(reservedKits).length && Object.keys(kitTypes).length ? (
          <Card className="kit-form-card">
            <CardBody>
              <div className="kit-form-card-header">
                <i className="now-ui-icons ui-1_check" />
                <span>{t('reserved-kits')}</span>
              </div>

              {Object.keys(kitTypes).map((k) => {
                const list = reservedKits[kitTypes[k].value];
                if (!list || !list.length) return null;
                return (
                  <div className="order-reserved-section" key={`rs-${k}`}>
                    <div className="order-reserved-type">{kitTypes[k].value}</div>
                    <div className="order-reserved-list">
                      {list.map((rk, j) => {
                        if (j === 5) {
                          return (
                            <div className="order-reserved-more" key={`rkm-${j}`}>
                              {t('nth-remaining').replace('###', list.length - j)}
                            </div>
                          );
                        }
                        if (j > 5) return null;
                        return <KitOverview key={`rk-${j}`} reservedKit={rk} />;
                      })} 
                    </div>
                  </div>
                );
              })}
            </CardBody>
          </Card>
        ) : null}

        {/* Sticky action bar */}
        <div className="kit-form-actions">
          <Button color="secondary" outline type="reset" onClick={(e) => { e.preventDefault(); history.goBack(); }}>
            <i className="now-ui-icons arrows-1_minimal-left" />
            {isEditing ? (t('return-btn') || 'Back') : (t('cancel-btn') || 'Cancel')}
          </Button>

          {isEditing && !isCancelled && (
            <Button
              color="info"
              outline
              disabled={kitDeleteLoading || edited}
              onClick={(e) => { e.preventDefault(); autoAssignKits(); }}
            >
              {kitDeleteLoading ? <Spinner size="sm" /> : <i className="now-ui-icons ui-1_check" />}
              {t('assign-btn')}
            </Button>
          )}

          {!isCancelled && (
            <Button
              form="order-form"
              color="primary"
              type="submit"
              disabled={isEditing ? (kitDeleteLoading || !edited) : !practicePick || !practicePick.length}
            >
              <i className="now-ui-icons ui-1_check" />
              {t('save-btn')}
            </Button>
          )}
        </div>

      </Form>
    </div>
  );
};

export default translate('practice-orders')(PracticeOrdersForm);
